"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Mp4WsPlayer = void 0;
const mobx_1 = require("mobx");
const adapter_1 = require("../../api/adapter");
const feature_1 = require("../../api/player/features/feature");
const errors_deprecated_1 = require("../errors-deprecated");
const browser_support_1 = require("../utils/browser-support");
const context_1 = require("../utils/context/context");
const uuid_1 = require("../utils/uuid");
const core_1 = require("./core");
const helper_1 = require("./helper");
const player_1 = require("./meow/player");
const user_agent_1 = require("../adapters/utils/user-agent");
const ACCEPTABLE_MODES = ["A", "B", "N"];
let sessionKeyCount = 0;
class Mp4WsPlayer extends core_1.CorePlayer {
    constructor(ctx, provider, options) {
        var _a;
        super(ctx, provider, options);
        this.data = { encodings: [], audioCodec: "", videoCodec: "" };
        this.player = null;
        this.sessionKey = (0, uuid_1.uuidv4)();
        this.origin = null;
        this.currentManifest = null;
        this.seekingOverHole = false;
        this.activeEncoding = null;
        this.estimatedKbps = null;
        this.playbackLock = false;
        this.nudgeInterval = undefined;
        this.nudgeTime = null;
        this.lastBufferEnd = null;
        this.fragFetchTime = 0;
        if (!adapter_1.device.isImplements(adapter_1.Feature.DEBUGGING) || !adapter_1.device.isImplements(adapter_1.Feature.MEDIA_DEVICE)) {
            throw new Error("Device is not supported");
        }
        (0, context_1.onceCanceled)(this.ctx).then((reason) => this.dispose(`Mp4Ws Player Context Cancelled: ${reason}`));
        this.addInnerDisposer(this.stop);
        this.device = adapter_1.device;
        const mse = this.options;
        if (mse.backFill == null || Number.isNaN(mse.backFill) || mse.backFill < 0 || mse.backFill > 10) {
            mse.backFill = 2;
        }
        if (mse.maxGap == null || Number.isNaN(mse.maxGap) || mse.maxGap < 0 || mse.maxGap > 10000) {
            mse.maxGap = 500;
        }
        if (mse.retry == null || Number.isNaN(mse.retry) || mse.retry < 0 || mse.retry > 30) {
            mse.retry = 20;
        }
        if (mse.requiredBuffer == null ||
            Number.isNaN(mse.requiredBuffer) ||
            mse.requiredBuffer < 0 ||
            mse.requiredBuffer > 10) {
            // seconds of buffer required for play to start
            mse.requiredBuffer = 1;
        }
        if (adapter_1.device.isEdge || adapter_1.device.isIE) {
            mse.backFill = Math.max(mse.backFill, 4);
            mse.requiredBuffer = Math.max(mse.requiredBuffer, 3);
        }
        if (mse.initFailTimeout == null || Number.isNaN(mse.initFailTimeout) || mse.initFailTimeout < 0) {
            mse.initFailTimeout = 30000;
        }
        if (mse.playbackFailTimeout == null || Number.isNaN(mse.playbackFailTimeout) || mse.playbackFailTimeout < 0) {
            mse.playbackFailTimeout = 60000;
        }
        if (mse.mode == null || !ACCEPTABLE_MODES.includes(mse.mode)) {
            mse.mode = "B";
        }
        // seconds of buffer required before removing buffer
        mse.requiredBufferBeforeRemove = mse.requiredBuffer;
        // ms interval to check current time against buffer and skip forward
        mse.skipWatchInterval = Math.floor(mse.requiredBuffer * 400);
        // seconds behind end of buffer before skipping forward to end of buffer - this.options.mse.requiredBuffer
        mse.skipForwardThreshold = 25;
        // download each playback attempt
        // adaptive, max shifts
        if (Number.isNaN((_a = mse.maxShifts) !== null && _a !== void 0 ? _a : NaN)) {
            mse.maxShifts = Infinity;
        }
        this.mse = this.options;
        this.logger.debug("meow constructor", { mse, meowMse: this.mse });
        this.on("currentQuality", (qty) => {
            var _a;
            this.setQuality(qty);
            (_a = this.player) === null || _a === void 0 ? void 0 : _a.setupTransport();
        });
        (0, mobx_1.autorun)((reaction) => {
            const el = this.elementSupervisor;
            const source = this.provider.source;
            if (el == null || source == null) {
                return;
            }
            reaction.dispose();
            this.handleSource(source);
            this.initSource(el);
        });
    }
    static get format() {
        return "mp4-ws";
    }
    get format() {
        return Mp4WsPlayer.format;
    }
    get currentLevel() {
        var _a, _b, _c, _d, _e;
        if (this.options.bitrate != null) {
            return this.getLevelForBitrate(this.options.bitrate);
        }
        if (this.activeEncoding == null) {
            return this.getLevelForBitrate((_a = this.options.estimatedKbps) !== null && _a !== void 0 ? _a : 0);
        }
        return this.getLevelForBitrate(((_c = (_b = this.activeEncoding) === null || _b === void 0 ? void 0 : _b.videoKbps) !== null && _c !== void 0 ? _c : 0) + ((_e = (_d = this.activeEncoding) === null || _d === void 0 ? void 0 : _d.audioKbps) !== null && _e !== void 0 ? _e : 0));
    }
    get originLocation() {
        const o = this.origin;
        if (o == null) {
            return null;
        }
        return this.addSessionKey(o.location);
    }
    /**
     * returns null if there is no ws endpoint available, otherwise returns the active ws url
     * @return {string} manifest url
     */
    get manifest() {
        var _a, _b;
        let l;
        if (this.options.origin) {
            l = this.originLocation;
            if (l == null) {
                this.options.origin = false;
                return this.manifest;
            }
        }
        else {
            const d = this.data;
            if (d.encodings.length === 0) {
                const o = this.originLocation;
                if (o != null) {
                    this.options.origin = true;
                }
                return o;
            }
            const e = this.pickEncoding((_b = (_a = this.options.bitrate) !== null && _a !== void 0 ? _a : this.estimatedKbps) !== null && _b !== void 0 ? _b : this.options.estimatedKbps);
            if (e == null) {
                return null;
            }
            l = e.location;
        }
        if (l == null) {
            return null;
        }
        return this.addSessionKey(l);
    }
    get location() {
        if (this.player == null) {
            return null;
        }
        return this.player.manifest;
    }
    set location(value) {
        // do nothing
    }
    get implementedFeatures() {
        return [feature_1.Feature.BITRATE_SWITCHING, feature_1.Feature.MUTED_AUTOPLAY];
    }
    static async isSupported(logger) {
        return (0, browser_support_1.supportsMp4)(logger);
    }
    async isSupported() {
        return Mp4WsPlayer.isSupported();
    }
    handleElTimeupdate() {
        var _a;
        super.handleElTimeupdate();
        const el = this.elementSupervisor;
        const buffered = (_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.buffered;
        if (el == null || buffered == null || buffered.length === 0) {
            return;
        }
        if (buffered.end(buffered.length - 1) - el.currentTime > this.mse.requiredBuffer) {
            if (adapter_1.device.isSafari) {
                el.playbackRate = 1.01;
            }
            else {
                el.playbackRate = 1.05;
            }
        }
        else {
            el.playbackRate = 1;
        }
    }
    handleElError() {
        var _a, _b, _c;
        super.handleElError();
        // what's code?
        const code = (_b = (_a = this.elementSupervisor) === null || _a === void 0 ? void 0 : _a.error) === null || _b === void 0 ? void 0 : _b.code;
        if (code == null) {
            return;
        }
        switch (code) {
            case 1:
                this.emitErrorDeprecated(new errors_deprecated_1.MediaError("MEDIA_ERR_ABORTED", { critical: true, eventTargetCode: code }));
                break;
            case 2:
                this.emitErrorDeprecated(new errors_deprecated_1.MediaError("MEDIA_ERR_NETWORK", { critical: true, eventTargetCode: code }));
                break;
            case 3:
                this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("MEDIA_ERR_DECODE", { critical: true, eventTargetCode: code }));
                break;
            case 4:
                this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("MEDIA_ERR_SRC_NOT_SUPPORTED", { critical: true, eventTargetCode: code }));
                break;
            default:
                if (!((_c = this.elementSupervisor) === null || _c === void 0 ? void 0 : _c.isSourceSet())) {
                    this.ctx.logger.error("meow unknown element error", {
                        manifest: this.manifest,
                    });
                }
                if (!this.localVideoPaused && !this.isDisposed) {
                    this.restart(false);
                }
        }
    }
    handleElPause() {
        super.handleElPause();
        if (this.provider.currentState === "offline") {
            this.stop();
        }
    }
    getLevelForBitrate(bitrate) {
        var _a, _b;
        const { encodings } = this.data;
        if (encodings.length === 0) {
            return null;
        }
        let i;
        for (i = 0; i < encodings.length; i++) {
            const encoding = encodings[i];
            if (encoding == null || encoding.videoWidth == null) {
                // eslint-disable-next-line no-continue
                continue;
            }
            if (((_a = encodings[i].videoKbps) !== null && _a !== void 0 ? _a : 0) + ((_b = encodings[i].audioKbps) !== null && _b !== void 0 ? _b : 0) > bitrate) {
                break;
            }
        }
        i = Math.max(i - 1, 0);
        return i;
    }
    /**
     * @param {number} [bitrate_] bitrate, defaults to options.bitrate || options.estimatedKbps
     * @return {object} encoding object
     */
    pickEncoding(bitrate_) {
        var _a;
        const bitrate = bitrate_ !== null && bitrate_ !== void 0 ? bitrate_ : this.options.bitrate;
        let { encodings } = this.data;
        if (encodings.length === 0) {
            return null;
        }
        if (bitrate == null) {
            return encodings[0];
        }
        encodings = encodings.sort((a, b) => { var _a, _b, _c, _d; return ((_a = a.audioKbps) !== null && _a !== void 0 ? _a : 0) + ((_b = a.videoKbps) !== null && _b !== void 0 ? _b : 0) < ((_c = b.audioKbps) !== null && _c !== void 0 ? _c : 0) + ((_d = b.videoKbps) !== null && _d !== void 0 ? _d : 0) ? 1 : -1; });
        const bestEncoding = encodings.find((e) => { var _a, _b; return ((_a = e.audioKbps) !== null && _a !== void 0 ? _a : 0) + ((_b = e.videoKbps) !== null && _b !== void 0 ? _b : 0) <= bitrate; });
        if (bestEncoding != null) {
            return bestEncoding;
        }
        return encodings[(_a = this.currentLevel) !== null && _a !== void 0 ? _a : 0];
    }
    setQuality(qty) {
        if ((qty === null || qty === void 0 ? void 0 : qty.encoding) == null) {
            this.ctx.logger.warn("encoding not found", { qty });
            return;
        }
        this.setEncoding(qty.encoding);
    }
    /**
     * calls to the transport to set the encoding
     * @param {object} encoding
     * @return {void}
     */
    setEncoding(encoding) {
        var _a, _b, _c, _d, _e;
        if (encoding == null ||
            (((_a = this.activeEncoding) === null || _a === void 0 ? void 0 : _a.videoKbps) === encoding.videoKbps && ((_b = this.activeEncoding) === null || _b === void 0 ? void 0 : _b.audioKbps) === encoding.audioKbps)) {
            return;
        }
        this.activeEncoding = encoding;
        this.estimatedKbps = ((_c = encoding.videoKbps) !== null && _c !== void 0 ? _c : 0) + ((_d = encoding.audioKbps) !== null && _d !== void 0 ? _d : 0);
        // safari does not handle
        if ((0, user_agent_1.safariLessThan14)(adapter_1.device)) {
            // The old version of Safari cannot gracefully change qualities
            this.stop();
            try {
                this.play().then(() => {
                    this.emit("bitrate-switch");
                });
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed after setEncoding()", {
                    inner,
                    player: this,
                }));
            }
            return;
        }
        const x = encoding.location.split("/");
        (_e = this.player) === null || _e === void 0 ? void 0 : _e.switchBitrate(x[x.length - 1]);
        this.emit("bitrate-switch");
    }
    /**
     * @param {number} bitrate
     * @return {void}
     */
    switchBitrate(bitrate) {
        this.ctx.logger.debug("meow switch bitrate", { bitrate });
        this.options.origin = false;
        this.options.bitrate = bitrate;
        this.emit("bitrate-switch"); // why are we emitting this twice here?
        this.stop();
        try {
            this.play().then(() => {
                this.emit("bitrate-switch"); // why are we emitting this twice here?
            });
        }
        catch (err) {
            const inner = err instanceof Error ? err : null;
            this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed after switchBitrate()", {
                inner,
                player: this,
            }));
        }
        // smooth shift.. maybe unnecessary
        // if (!bitrate) {
        // 	this.options.bitrate = null;
        // 	this.stop(() => {
        // 		this.play();
        // 	});
        // 	return;
        // }
        // this.setEncoding(this.pickEncoding(bitrate));
    }
    addSessionKey(l) {
        if (l.includes("sid")) {
            return l;
        }
        sessionKeyCount++;
        const delimiter = !l.includes("?") ? "?" : "&";
        const newLStr = `${l}${delimiter}sid=${this.sessionKey}&c=${sessionKeyCount}`;
        return newLStr;
    }
    pause() {
        this.stop();
    }
    stop() {
        this.playbackLock = false;
        clearInterval(this.nudgeInterval);
        if (this.player == null) {
            return;
        }
        this.player.once("disposed", () => {
            this.player = null;
        });
    }
    // call play on the parent class that interacts with the video element
    async corePlay() {
        return super.play(true);
    }
    async play() {
        const el = this.elementSupervisor;
        if (el == null) {
            throw new Error(helper_1.ERRORS.ELEMENT_REQUIRED);
        }
        if (this.playbackLock) {
            return !el.paused;
        }
        this.playbackLock = true;
        if (this.options.autoPlay && this.localVideoPaused) {
            setTimeout(() => {
                this.localVideoPaused = false;
            });
        }
        if (this.player != null) {
            return this.player.play().then(() => !el.paused);
        }
        this.ctx.logger.debug("meow driver play received");
        return !this.localVideoPaused;
    }
    handleSource(manifest) {
        this.ctx.logger.debug("mp4-ws: handleSource()");
        const format = manifest === null || manifest === void 0 ? void 0 : manifest.formats["mp4-ws"];
        if (format == null) {
            this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("manifest doesn't contains 'mp4-hls' format", { manifest }));
            return;
        }
        if (this.data.encodings.length === 0) {
            this.data = format;
        }
    }
    initSource(el) {
        if (this.player != null) {
            // if the player is already created,
            // it means another play() call was made before
            // the source event was fired
            return;
        }
        clearInterval(this.nudgeInterval);
        this.nudgeTime = null;
        let nudges = 0;
        this.nudgeInterval = adapter_1.device.setInterval(() => {
            if (this.localVideoPaused || el.currentTime === 0) {
                return;
            }
            if (this.nudgeTime != null &&
                el.currentTime === this.nudgeTime &&
                (this.lastBufferEnd == null || el.currentTime < this.lastBufferEnd)) {
                this.ctx.logger.debug("meow playback halted, nudging");
                nudges++;
                el.currentTime += 0.1;
                const promise = this.corePlay();
                if (promise.then != null) {
                    promise.catch(() => {
                        // its ok, it didn't work out this time
                    });
                }
            }
            else {
                nudges = 0;
                this.nudgeTime = el.currentTime;
            }
            if (nudges > 29) {
                clearInterval(this.nudgeInterval);
                this.ctx.logger.debug("meow too many halt nudges, restart");
                this.restart(false);
            }
        }, 500);
        const source = this.provider.source;
        if (source != null) {
            setTimeout(() => {
                var _a;
                (_a = this.provider) === null || _a === void 0 ? void 0 : _a.emit("source", source);
            });
        }
        this.ctx.logger.debug("meow manifest verified, player created");
        if (this.elementSupervisor == null) {
            throw new Error(helper_1.ERRORS.ELEMENT_REQUIRED);
        }
        this.player = new player_1.MeowPlayer(this.ctx, this, this.options, Reflect.get(this.elementSupervisor, "element"));
        this.counters.upshift = 0;
        this.counters.downshift = 0;
        this.counters.currentErrorCount = 0;
        this.counters.fragCounts = 0;
        this.counters.fragSize = 0;
        this.counters.fragFetchTime = 0;
        this.counters.fragMaxTime = 0;
        this.counters.fragMinTime = 0;
        this.player.on("stop", () => {
            this.stop();
        });
        this.player.on("no-audio", () => {
            this.emit("no-audio");
        });
        this.player.on("upshift", () => {
            this.counters.upshift++;
        });
        this.player.on("downshift", () => {
            this.counters.downshift++;
        });
        this.player.on("fragment", (data) => {
            var _a;
            this.counters.fragCounts++;
            this.counters.fragSize += data.size;
            this.counters.fragFetchTime += data.time;
            this.counters.fragMaxTime = Math.max(this.counters.fragMaxTime, data.time);
            this.counters.fragMinTime = Math.min((_a = this.counters.fragMinTime) !== null && _a !== void 0 ? _a : data.time, data.time);
        });
        this.player.on("error", (data) => {
            this.counters.currentErrorCount++;
            if (data.notSupported) {
                this.emitErrorDeprecated(new errors_deprecated_1.DriverNotSupportedError("player error", {
                    data: {
                        fatal: data.fatal,
                        message: data.message,
                        notSupported: data.notSupported,
                        err: (0, errors_deprecated_1.wrapNativeError)(data.err),
                    },
                }));
                return;
            }
            if (data.fatal) {
                this.ctx.logger.debug("meow player fatal error, restarting");
                this.restart(false);
            }
        });
        if (this.device.hidden) {
            this.device.addEventListener("visibilitychange", () => {
                var _a;
                try {
                    (_a = this.player) === null || _a === void 0 ? void 0 : _a.play();
                }
                catch (err) {
                    const inner = err instanceof Error ? err : null;
                    this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed after visibilitychange event triggered", {
                        inner,
                        player: this,
                    }));
                }
            });
        }
        else {
            try {
                if (!this.pausedOnUnmount) {
                    this.player.play();
                }
            }
            catch (err) {
                const inner = err instanceof Error ? err : null;
                this.emitErrorDeprecated(new errors_deprecated_1.PlayingIssueError("play failed", {
                    inner,
                    player: this,
                }));
            }
        }
    }
}
exports.Mp4WsPlayer = Mp4WsPlayer;
Mp4WsPlayer.displayName = "Mp4WsPlayer";
//# sourceMappingURL=mp4-ws.js.map